import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import { client } from "../services/axiosClient";

// ─── Persistência de filtros entre views ──────────────────────────────────────
// Armazena no sessionStorage um objeto compartilhado (merge por view).
// Ambas as chaves ficam no sessionStorage: duram a sessão da aba e são zeradas ao trocar de implementação.

const PERSIST_PREF_KEY = "rfb_persist_pref";
const PERSIST_STATE_KEY = "rfb_filters_v1";

/**
 * Limpa todo o estado de filtros e a preferência do toggle "manter filtros".
 * Ambos vivem no sessionStorage, então esta chamada zera tudo de uma vez.
 * Deve ser chamado ao trocar de implementação.
 */
export function clearPersistedFilters() {
  try {
    sessionStorage.removeItem(PERSIST_STATE_KEY);
    sessionStorage.removeItem(PERSIST_PREF_KEY);
  } catch (_e) {
    // ignore
  }
}

function loadPersistedFilters() {
  try {
    return (
      JSON.parse(sessionStorage.getItem(PERSIST_STATE_KEY) || "null") || {}
    );
  } catch (_e) {
    return {};
  }
}
function savePersistedFilters(data) {
  try {
    sessionStorage.setItem(PERSIST_STATE_KEY, JSON.stringify(data));
  } catch (_e) {
    // no-op
  }
}
function loadPersistPref() {
  try {
    const v = sessionStorage.getItem(PERSIST_PREF_KEY);
    return v === null ? false : v === "true"; // default: false
  } catch {
    return false;
  }
}
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Unified filter state and logic for Results views
 * (PLExplorer, GraphExplorer, MonthlyPL, LogisticsFlow).
 *
 * @param {object}   opts
 * @param {string}   opts.metaEndpoint       Endpoint que retorna { filters: { list, names, items, groups } }
 *                                           Default: "api/results_pl_explorer_meta/"
 * @param {string}   opts.periodDefaultMode  "group" | "items" | ""  Default: "group"
 * @param {string}   opts.periodDefaultValue Chave usada quando mode=group.  Default: "l12m"
 * @param {boolean}  opts.includePeriod      Se false, "period" é removido da lista de filtros da barra.
 *                                           Usar para Monthly P&L (período controlado por `lastMonth`).
 *                                           Default: true
 * @param {function} opts.onMetaLoaded       Callback opcional (response) após carga bem-sucedida do meta.
 *                                           Útil para views que extraem PL structure, currency, etc. da mesma resposta.
 */
export function useResultsFilters({
  metaEndpoint = "api/results_pl_explorer_meta/",
  periodDefaultMode = "group",
  periodDefaultValue = "l12m",
  includePeriod = true,
  onMetaLoaded,
  getDimInfoExtra,
} = {}) {
  const { t, i18n } = useTranslation();

  // ── ref para callback que enriquece informações de dimensão (ex.: fixedRefLine) ──
  const getDimInfoExtraRef = useRef(getDimInfoExtra);
  getDimInfoExtraRef.current = getDimInfoExtra;

  // ---------- refs de metadados ----------
  const filtersList = useRef([]);
  const filtersNames = useRef({});
  const filtersItems = useRef({});
  const filtersGroups = useRef({});
  const filtersDimensionGroups = useRef([]); // [[dim1,dim2], [dim3,dim4], ...]

  // ---------- estado dos filtros comuns ----------
  // Período é pre-inicializado com o default para que activePreset seja correto
  // desde o primeiro render, sem aguardar o carregamento do meta.
  const [commonFilters, setCommonFilters] = useImmer(() =>
    includePeriod && periodDefaultMode === "group"
      ? { period: [periodDefaultValue] }
      : {},
  );
  const [commonFiltersIndexes, setCommonFiltersIndexes] = useImmer({});
  const [commonFiltersMode, setCommonFiltersMode] = useImmer(() =>
    includePeriod && periodDefaultMode === "group"
      ? { period: periodDefaultMode }
      : {},
  );
  const [commonFiltersSequence, setCommonFiltersSequence] = useState(
    includePeriod ? ["period"] : [],
  );

  // ---------- filtros por range (maior/menor/entre) ----------
  // { [filterKey]: { mode: "gt"|"lt"|"between", min, max, refLine, label } }
  const [rangeFilters, setRangeFilters] = useImmer({});

  // ---------- persistência de filtros ----------
  const [persist, setPersist] = useState(loadPersistPref);

  // Salva preferência "manter filtros" no sessionStorage quando muda
  useEffect(() => {
    try {
      sessionStorage.setItem(PERSIST_PREF_KEY, String(persist));
    } catch (_e) {
      // no-op
    }
  }, [persist]);

  // ---------- carregamento do meta ----------
  const [metaLoaded, setMetaLoaded] = useState(false);

  useEffect(() => {
    client
      .post(metaEndpoint)
      .then((response) => {
        const filters = response.data.filters;
        const list = includePeriod
          ? filters.list
          : filters.list.filter((f) => f !== "period");

        filtersList.current = list;
        filtersNames.current = filters.names;
        filtersItems.current = filters.items;
        filtersGroups.current = filters.groups;
        filtersDimensionGroups.current = filters.dimension_groups || [];

        const initF = {};
        const initM = {};
        const initI = {};
        Object.keys(filters.names).forEach((n) => {
          if (n === "period" && includePeriod) {
            initF[n] =
              periodDefaultMode === "group" ? [periodDefaultValue] : [];
            initM[n] = periodDefaultMode;
            initI[n] = periodDefaultMode === "group" ? 0 : [];
          } else {
            initF[n] = [];
            initM[n] = "";
            initI[n] = [];
          }
        });
        setCommonFilters(() => initF);
        setCommonFiltersMode(() => initM);
        setCommonFiltersIndexes(() => initI);

        // Restauração de filtros persistidos: sobrescreve initF com o estado salvo,
        // mas só para chaves que existem nesta view (merge seguro entre views).
        // A referência a `persist` aqui usa o valor do closure — como o efeito só
        // corre uma vez (deps=[]), precisamos ler a preferência direto do storage.
        if (loadPersistPref()) {
          const stored = loadPersistedFilters();
          if (stored.filters) {
            const restoredF = {};
            const restoredM = {};
            const restoredI = {};
            list.forEach((k) => {
              if (stored.filters[k] !== undefined) {
                restoredF[k] = stored.filters[k];
                restoredM[k] = stored.filtersMode?.[k] || "";
                restoredI[k] = [];
              }
            });
            const hasAny = list.some((k) => restoredF[k]?.length > 0);
            if (hasAny) {
              // Merge sobre initF: sobrescreve só as keys com valor restaurado
              setCommonFilters((d) => {
                Object.assign(d, restoredF);
              });
              setCommonFiltersMode((d) => {
                Object.assign(d, restoredM);
              });
              const restoredSeq = (stored.filtersSequence || []).filter(
                (k) => list.includes(k) && restoredF[k]?.length > 0,
              );
              if (restoredSeq.length > 0) {
                setCommonFiltersSequence(restoredSeq);
              }
            }
          }
          // Restaura range filters
          if (stored.rangeFilters) {
            const restoredRF = {};
            Object.entries(stored.rangeFilters).forEach(([k, v]) => {
              if (list.includes(k)) restoredRF[k] = v;
            });
            if (Object.keys(restoredRF).length > 0) {
              setRangeFilters((d) => {
                Object.assign(d, restoredRF);
              });
            }
          }
        }

        if (onMetaLoaded) onMetaLoaded(response);
        setMetaLoaded(true);
      })
      .catch(() => setMetaLoaded(true));
  }, []);

  // ---------- estado do modal de filtro ----------
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterModalCurrentFilter, setFilterModalCurrentFilter] = useState("");
  const [filterModalFilterName, setFilterModalFilterName] = useState("");
  const [filterModalItemsChoices, setFilterModalItemsChoices] = useState([]);
  const [filterModalItemsSelected, setFilterModalItemsSelected] = useState([]);
  const [filterModalGroupChoices, setFilterModalGroupChoices] = useState([]);
  const [filterModalGroupSelected, setFilterModalGroupSelected] = useState("");
  const [filterModalFilterMode, setFilterModalFilterMode] = useState("");

  // ---------- helpers ----------

  /**
   * Converte rawItems (vindo do estado) para labels legíveis.
   * Especial para "period" em modo items: formata como "Jan-24".
   */
  const getFilterItems = useCallback(
    (rawItems, filter, mode) => {
      if (filter === "period" && mode === "items") {
        return rawItems.map(
          (m) =>
            t("months." + m[1].toString()) + "-" + m[0].toString().slice(-2),
        );
      }
      return rawItems;
    },
    [t],
  );

  /**
   * Converte todos os itens disponíveis de um filtro para labels legíveis.
   * Usado para preencher as escolhas do modal.
   */
  const getChoicesList = useCallback(
    (filter, items) => {
      if (filter === "period") {
        return items.map(
          (m) =>
            t("months." + m[1].toString()) + "-" + m[0].toString().slice(-2),
        );
      }
      return items;
    },
    [t],
  );

  const filtersListTranslated = useCallback(
    () =>
      filtersList.current.map(
        (f) => filtersNames.current[f]?.[i18n.language] || f,
      ),
    [i18n.language],
  );

  const filtersActiveList = useMemo(
    () =>
      filtersList.current.reduce((acc, f, i) => {
        if (commonFilters[f]?.length > 0) acc.push(i);
        return acc;
      }, []),
    [commonFilters],
  );

  const filtersSequenceNums = useCallback(
    () =>
      filtersList.current.map((f) => {
        const p = commonFiltersSequence.indexOf(f);
        return p === -1 ? "" : p > 9 ? "N" : (p + 1).toString();
      }),
    [commonFiltersSequence],
  );

  const getFiltersInSequence = useCallback(
    () =>
      commonFiltersSequence.filter((f) =>
        Object.keys(filtersNames.current).includes(f),
      ),
    [commonFiltersSequence],
  );

  // ---------- abrir modal para um filtro ----------

  const openFilterModal = useCallback(
    (filterKey) => {
      setFilterModalCurrentFilter(filterKey);
      setFilterModalFilterName(
        filtersNames.current[filterKey]?.[i18n.language] || filterKey,
      );

      if (Object.keys(filtersGroups.current).includes(filterKey)) {
        setFilterModalGroupChoices(
          Object.entries(filtersGroups.current[filterKey]).map(([k, v]) => [
            k,
            v[i18n.language],
          ]),
        );
      } else {
        setFilterModalGroupChoices([]);
      }

      const all = filtersItems.current[filterKey] || [];
      setFilterModalItemsChoices(
        getChoicesList(filterKey, all).map((d, i) => ({
          display: d,
          originalIndex: i,
        })),
      );

      setFilterModalFilterMode(commonFiltersMode[filterKey] || "");
      if (commonFiltersMode[filterKey] === "items") {
        setFilterModalGroupSelected("");
        setFilterModalItemsSelected(commonFiltersIndexes[filterKey] || []);
      } else if (commonFiltersMode[filterKey] === "group") {
        setFilterModalItemsSelected([]);
        setFilterModalGroupSelected(commonFilters[filterKey]?.[0] || "");
      } else {
        setFilterModalGroupSelected("");
        setFilterModalItemsSelected([]);
      }

      setFilterModalOpen(true);
    },
    [
      i18n.language,
      commonFilters,
      commonFiltersIndexes,
      commonFiltersMode,
      getChoicesList,
    ],
  );

  /**
   * Compatível com a prop `onClick` do FilterDropdown:
   * recebe índice numérico (posição na lista) ou string (nome do filtro).
   * Retorna um handler curried para manter a mesma assinatura que as views já usavam.
   */
  const filterMenuClickHandler = useCallback(
    () => (event) => {
      const sf = typeof event === "string" ? event : filtersList.current[event];
      openFilterModal(sf);
    },
    [openFilterModal],
  );

  // ---------- aplicar seleção do modal ----------

  const handleFilterApply = useCallback(() => {
    const f = filterModalCurrentFilter;
    if (
      filterModalFilterMode === "items" ||
      filterModalGroupChoices.length === 0
    ) {
      setCommonFiltersIndexes((d) => {
        d[f] = filterModalItemsSelected;
      });
      setCommonFilters((d) => {
        d[f] = filterModalItemsSelected.map((i) => filtersItems.current[f][i]);
      });
      setCommonFiltersMode((d) => {
        d[f] = "items";
      });
      if (!commonFiltersSequence.includes(f))
        setCommonFiltersSequence((s) => [...s, f]);
    } else if (filterModalFilterMode === "group") {
      setCommonFiltersIndexes((d) => {
        d[f] = [];
      });
      setCommonFilters((d) => {
        d[f] = [filterModalGroupSelected];
      });
      setCommonFiltersMode((d) => {
        d[f] = "group";
      });
      if (!commonFiltersSequence.includes(f))
        setCommonFiltersSequence((s) => [...s, f]);
    }
    setFilterModalOpen(false);
  }, [
    filterModalCurrentFilter,
    filterModalFilterMode,
    filterModalGroupChoices.length,
    filterModalItemsSelected,
    filterModalGroupSelected,
    commonFiltersSequence,
    setCommonFilters,
    setCommonFiltersIndexes,
    setCommonFiltersMode,
  ]);

  // ---------- limpar filtro pelo modal ----------

  const handleFilterClear = useCallback(() => {
    const f = filterModalCurrentFilter;
    if (f === "period") return;
    setCommonFilters((d) => {
      d[f] = [];
    });
    setCommonFiltersIndexes((d) => {
      d[f] = [];
    });
    setCommonFiltersMode((d) => {
      d[f] = "";
    });
    setCommonFiltersSequence((s) => s.filter((x) => x !== f));
    // Limpa range filter interno se houver
    setRangeFilters((d) => {
      delete d[f];
    });
    setFilterModalOpen(false);
  }, [
    filterModalCurrentFilter,
    setCommonFilters,
    setCommonFiltersIndexes,
    setCommonFiltersMode,
  ]);

  // ---------- remover item de uma tag ativa ----------

  const removeFilter = useCallback(
    (filter, item, index, all = false) => {
      if (filter === "period" && commonFiltersMode[filter] === "items") {
        const mi = filtersItems.current[filter][index];
        const next = all
          ? []
          : commonFilters[filter].filter(
              (i) => JSON.stringify(i) !== JSON.stringify(mi),
            );
        if (next.length === 0) return; // período exige ao menos 1 item
        setCommonFilters((d) => {
          d[filter] = next;
        });
        setCommonFiltersIndexes((d) => {
          d[filter] = (commonFiltersIndexes[filter] || []).filter(
            (i) => i !== index,
          );
        });
      } else {
        const next = all ? [] : commonFilters[filter].filter((i) => i !== item);
        setCommonFilters((d) => {
          d[filter] = next;
        });
        if (next.length === 0) {
          setCommonFiltersIndexes((d) => {
            d[filter] = [];
          });
          setCommonFiltersMode((d) => {
            d[filter] = "";
          });
          setCommonFiltersSequence((s) => s.filter((x) => x !== filter));
        } else {
          setCommonFiltersIndexes((d) => {
            d[filter] = (commonFiltersIndexes[filter] || []).filter(
              (i) => i !== index,
            );
          });
        }
      }
    },
    [
      commonFilters,
      commonFiltersIndexes,
      commonFiltersMode,
      setCommonFilters,
      setCommonFiltersIndexes,
      setCommonFiltersMode,
    ],
  );

  // ---------- handlers internos do modal ----------

  const handleFilterModalGroupMenuSelect = useCallback((event) => {
    setFilterModalFilterMode("group");
    setFilterModalGroupSelected(event.target.value);
    setFilterModalItemsSelected([]);
  }, []);

  const handleFilterModalItemsSelectNone = useCallback(() => {
    setFilterModalItemsSelected([]);
    if (filterModalFilterMode === "items") setFilterModalFilterMode("");
  }, [filterModalFilterMode]);

  // ---------- helpers de período para FilterBarExtras ----------

  /**
   * Lista de períodos disponíveis no formato { year, month } esperado por PeriodFilterModal.
   * Derivado de filtersItems.current.period (array de tuplas [year, month]).
   */
  const availablePeriods = useMemo(
    () =>
      (filtersItems.current.period || []).map(([year, month]) => ({
        year,
        month,
      })),
    [metaLoaded], // filtersItems é uma ref; metaLoaded como proxy de quando ela foi populada
  );

  /**
   * Set de chaves "year-month" a partir do estado atual do filtro de período.
   * Retorna null quando o modo é "group" (PeriodFilterModal usa initialPreset).
   */
  const selectedPeriodKeys = useMemo(() => {
    if (!includePeriod) return null;
    const mode = commonFiltersMode.period;
    const values = commonFilters.period || [];
    if (mode === "items" && values.length > 0) {
      return new Set(values.map(([y, m]) => `${y}-${m}`));
    }
    return null;
  }, [commonFilters.period, commonFiltersMode.period, includePeriod]);

  /**
   * Preset ativo (ex.: "l12m") quando o período está em modo "group".
   */
  const activePreset = useMemo(() => {
    if (!includePeriod) return null;
    if (commonFiltersMode.period === "group")
      return commonFilters.period?.[0] ?? null;
    return null;
  }, [commonFilters.period, commonFiltersMode.period, includePeriod]);

  /**
   * Aplica o resultado de PeriodFilterModal ao estado do hook.
   * Aceita { selectedKeys: Set<"year-month">, activePreset: string|null }.
   */
  const handlePeriodApply = useCallback(
    ({ selectedKeys, activePreset: preset }) => {
      if (preset) {
        setCommonFilters((d) => {
          d.period = [preset];
        });
        setCommonFiltersMode((d) => {
          d.period = "group";
        });
        setCommonFiltersIndexes((d) => {
          d.period = 0;
        });
      } else if (selectedKeys && selectedKeys.size > 0) {
        const allPeriods = filtersItems.current.period || [];
        const tuples = [...selectedKeys]
          .map((k) => {
            const [y, m] = k.split("-").map(Number);
            return (
              allPeriods.find(([py, pm]) => py === y && pm === m) || [y, m]
            );
          })
          .sort((a, b) => a[0] * 12 + a[1] - (b[0] * 12 + b[1]));
        const indexes = tuples
          .map((t) =>
            allPeriods.findIndex(([py, pm]) => py === t[0] && pm === t[1]),
          )
          .filter((i) => i !== -1);
        setCommonFilters((d) => {
          d.period = tuples;
        });
        setCommonFiltersMode((d) => {
          d.period = "items";
        });
        setCommonFiltersIndexes((d) => {
          d.period = indexes;
        });
      }
    },
    [setCommonFilters, setCommonFiltersMode, setCommonFiltersIndexes],
  );

  /**
   * Redefine todos os filtros para os valores padrão da view.
   */
  const handleResetFilters = useCallback(() => {
    setCommonFilters((d) => {
      Object.keys(d).forEach((k) => {
        d[k] =
          k === "period" && includePeriod
            ? periodDefaultMode === "group"
              ? [periodDefaultValue]
              : []
            : [];
      });
    });
    setCommonFiltersMode((d) => {
      Object.keys(d).forEach((k) => {
        d[k] = k === "period" && includePeriod ? periodDefaultMode : "";
      });
    });
    setCommonFiltersIndexes((d) => {
      Object.keys(d).forEach((k) => {
        d[k] =
          k === "period" && includePeriod && periodDefaultMode === "group"
            ? 0
            : [];
      });
    });
    setCommonFiltersSequence(includePeriod ? ["period"] : []);
    // Limpa estado persistido desta view
    if (persist) {
      const stored = loadPersistedFilters();
      filtersList.current.forEach((k) => {
        if (stored.filters) stored.filters[k] = [];
        if (stored.filtersMode) stored.filtersMode[k] = "";
      });
      stored.filtersSequence = (stored.filtersSequence || []).filter(
        (k) => !filtersList.current.includes(k),
      );
      // Limpa range filters persistidos
      if (stored.rangeFilters) {
        filtersList.current.forEach((k) => {
          delete stored.rangeFilters[k];
        });
      }
      savePersistedFilters(stored);
    }
    // Limpa range filters no estado
    setRangeFilters(() => ({}));
  }, [
    includePeriod,
    periodDefaultMode,
    periodDefaultValue,
    persist,
    setCommonFilters,
    setCommonFiltersMode,
    setCommonFiltersIndexes,
    setRangeFilters,
  ]);

  // Salva estado dos filtros no sessionStorage quando persist=true e meta já carregou.
  // Usa merge: só atualiza chaves desta view, preservando chaves de outras views.
  useEffect(() => {
    if (!metaLoaded || !persist) return;
    const stored = loadPersistedFilters();
    if (!stored.filters) stored.filters = {};
    if (!stored.filtersMode) stored.filtersMode = {};
    filtersList.current.forEach((k) => {
      stored.filters[k] = commonFilters[k] || [];
      stored.filtersMode[k] = commonFiltersMode[k] || "";
    });
    // Sequence: mantém entradas de outras views, adiciona as desta view
    const otherSeq = (stored.filtersSequence || []).filter(
      (k) => !filtersList.current.includes(k),
    );
    const thisSeq = commonFiltersSequence.filter((k) =>
      filtersList.current.includes(k),
    );
    stored.filtersSequence = [...otherSeq, ...thisSeq];
    // Range filters: merge por chave
    if (!stored.rangeFilters) stored.rangeFilters = {};
    // Remove chaves desta view que não estão mais ativas
    filtersList.current.forEach((k) => {
      if (!rangeFilters[k]) delete stored.rangeFilters[k];
    });
    Object.assign(stored.rangeFilters, rangeFilters);
    savePersistedFilters(stored);
  }, [commonFilters, commonFiltersMode, commonFiltersSequence, rangeFilters, persist, metaLoaded]); // eslint-disable-line

  // ---------- helpers para DimFilterDialog ----------

  /**
   * Constrói o objeto `dim` esperado por DimFilterDialog para um filterKey.
   * groups é traduzido para { key: label } no idioma atual.
   */
  const getDimInfo = useCallback(
    (filterKey) => ({
      key: filterKey,
      label: filtersNames.current[filterKey]?.[i18n.language] || filterKey,
      items: filtersItems.current[filterKey] || [],
      groups: filtersGroups.current[filterKey]
        ? Object.fromEntries(
            Object.entries(filtersGroups.current[filterKey]).map(([k, v]) => [
              k,
              v[i18n.language] || k,
            ]),
          )
        : null,
      hasRefRange: false,
      refLineKey: null,
      ...(getDimInfoExtraRef.current?.(filterKey) || {}),
    }),
    [i18n.language],
  );

  /**
   * Constrói o objeto `existingFilter` para DimFilterDialog,
   * reconstruindo a partir do estado atual do filtro.
   */
  const getExistingFilterForDim = useCallback(
    (filterKey) => {
      const mode = commonFiltersMode[filterKey];
      const values = commonFilters[filterKey] || [];

      if (mode === "items" && values.length > 0) {
        return {
          mode: "items",
          items: values,
          multiSelect: values.length > 1,
        };
      }
      if (mode === "group" && values.length > 0) {
        const groupKey = values[0];
        const groupLabel =
          filtersGroups.current[filterKey]?.[groupKey]?.[i18n.language] ||
          groupKey;
        return {
          mode: "group",
          groupKey,
          items: [groupLabel],
        };
      }

      // Range filter interno
      const rf = rangeFilters[filterKey];
      if (rf) {
        return {
          mode: "range",
          rangeMode: rf.mode,
          rangeMin: rf.min !== null ? String(rf.min) : "",
          rangeMax: rf.max !== null ? String(rf.max) : "",
          refLine: rf.refLine,
        };
      }

      return null;
    },
    [commonFilters, commonFiltersMode, rangeFilters, i18n.language],
  );

  /**
   * Aplica a seleção retornada por DimFilterDialog.
   * Aceita { mode, items[], groupKey?, multiSelect?, rangeMode?, rangeMin?, rangeMax?, refLine? }.
   */
  const handleDimFilterApply = useCallback(
    (filterObj) => {
      const f = filterModalCurrentFilter;
      if (!f) return;

      // Range: armazena no estado interno de range filters
      // e remove qualquer filtro por itens/grupo da mesma dimensão
      if (filterObj.mode === "range") {
        setRangeFilters((d) => {
          d[f] = {
            mode: filterObj.rangeMode,
            min:
              filterObj.rangeMin !== "" ? parseFloat(filterObj.rangeMin) : null,
            max:
              filterObj.rangeMax !== "" ? parseFloat(filterObj.rangeMax) : null,
            refLine: filterObj.refLine || null,
            label: filterObj.items?.[0] || "",
          };
        });
        // Remove filtro por itens/grupo da mesma dimensão, se existir
        setCommonFilters((d) => {
          delete d[f];
        });
        setCommonFiltersIndexes((d) => {
          delete d[f];
        });
        setCommonFiltersMode((d) => {
          delete d[f];
        });
        setCommonFiltersSequence((s) => s.filter((k) => k !== f));
        setFilterModalOpen(false);
        return;
      }

      if (filterObj.mode === "items") {
        const allItems = filtersItems.current[f] || [];
        const indexes = filterObj.items
          .map((v) => allItems.indexOf(v))
          .filter((i) => i !== -1);
        // Remove filtro por range da mesma dimensão, se existir
        setRangeFilters((d) => {
          delete d[f];
        });
        setCommonFilters((d) => {
          d[f] = filterObj.items;
        });
        setCommonFiltersIndexes((d) => {
          d[f] = indexes;
        });
        setCommonFiltersMode((d) => {
          d[f] = "items";
        });
        if (!commonFiltersSequence.includes(f))
          setCommonFiltersSequence((s) => [...s, f]);
      } else if (filterObj.mode === "group") {
        // Remove filtro por range da mesma dimensão, se existir
        setRangeFilters((d) => {
          delete d[f];
        });
        setCommonFilters((d) => {
          d[f] = [filterObj.groupKey];
        });
        setCommonFiltersIndexes((d) => {
          d[f] = [];
        });
        setCommonFiltersMode((d) => {
          d[f] = "group";
        });
        if (!commonFiltersSequence.includes(f))
          setCommonFiltersSequence((s) => [...s, f]);
      }
      setFilterModalOpen(false);
    },
    [
      filterModalCurrentFilter,
      commonFiltersSequence,
      setCommonFilters,
      setCommonFiltersIndexes,
      setCommonFiltersMode,
      setRangeFilters,
    ],
  );

  // Bundle de props para <DimFilterDialog>
  const dimFilterDialogProps = {
    open: filterModalOpen,
    dim: filterModalCurrentFilter ? getDimInfo(filterModalCurrentFilter) : null,
    existingFilter: filterModalCurrentFilter
      ? getExistingFilterForDim(filterModalCurrentFilter)
      : null,
    onApply: handleDimFilterApply,
    onClear: handleFilterClear,
    onClose: () => setFilterModalOpen(false),
  };

  // ---------- bundle de props para <FilterDialog> (legado) ----------
  // Pronto para spread: <FilterDialog {...filterDialogProps} clientColor={...} />

  const filterDialogProps = {
    open: filterModalOpen,
    onClose: () => setFilterModalOpen(false),
    headerTitle: filterModalFilterName,
    headerScope: null,
    selectedItems: filterModalItemsSelected,
    onItemsSelect: (indexes) => {
      setFilterModalItemsSelected(indexes);
      setFilterModalFilterMode(indexes.length > 0 ? "items" : "");
    },
    onSelectNone: handleFilterModalItemsSelectNone,
    onClearFilter: handleFilterClear,
    onApply: handleFilterApply,
    groupChoices: filterModalGroupChoices,
    groupSelected: filterModalGroupSelected,
    onGroupSelect: handleFilterModalGroupMenuSelect,
    filterMode: filterModalFilterMode,
    allItems: filterModalItemsChoices,
  };

  // ---------- bundle de seções de dimensão para o DimDropdown ----------
  // Lista de listas [[dim1,dim2],[dim3,dim4]] → [{key, filterKeys[]}]
  // Sem labels — o dropdown usa apenas separadores visuais entre grupos.
  const dimSections = useMemo(() => {
    return (filtersDimensionGroups.current || []).map((dims, i) => ({
      key: `g${i}`,
      filterKeys: Array.isArray(dims) ? dims : dims.dimensions || [],
    }));
  }, [metaLoaded]);

  // ---------- helper para limpar um range filter individualmente ----------
  const clearRangeFilter = useCallback(
    (filterKey) => {
      setRangeFilters((d) => {
        delete d[filterKey];
      });
    },
    [setRangeFilters],
  );

  // ---------- retorno ----------

  return {
    // metadados (refs)
    filtersList,
    filtersNames,
    filtersItems,
    filtersGroups,
    // estado
    metaLoaded,
    commonFilters,
    setCommonFilters,
    commonFiltersIndexes,
    setCommonFiltersIndexes,
    commonFiltersMode,
    setCommonFiltersMode,
    commonFiltersSequence,
    setCommonFiltersSequence,
    // estado do modal (exposto para casos especiais, ex.: PLExplorer com scope)
    filterModalOpen,
    setFilterModalOpen,
    filterModalCurrentFilter,
    filterModalFilterMode,
    filterModalGroupChoices,
    filterModalItemsSelected,
    filterModalGroupSelected,
    setFilterModalFilterMode,
    setFilterModalItemsSelected,
    setFilterModalGroupSelected,
    // helpers
    getFilterItems,
    getChoicesList,
    filtersListTranslated,
    filtersActiveList,
    filtersSequenceNums,
    getFiltersInSequence,
    // handlers
    openFilterModal,
    filterMenuClickHandler,
    handleFilterApply,
    handleFilterClear,
    handleDimFilterApply,
    handlePeriodApply,
    handleResetFilters,
    removeFilter,
    handleFilterModalGroupMenuSelect,
    handleFilterModalItemsSelectNone,
    // helpers de período (para FilterBarExtras)
    availablePeriods,
    selectedPeriodKeys,
    activePreset,
    // bundle pronto para FilterDialog (legado)
    filterDialogProps,
    // bundle pronto para DimFilterDialog
    dimFilterDialogProps,
    getDimInfo,
    getExistingFilterForDim,
    // seções de dimensão (para DimDropdown)
    dimSections,
    // range filters (maior/menor/entre) — estado interno
    rangeFilters,
    clearRangeFilter,
    // persistência de filtros entre views
    persist,
    setPersist,
  };
}
